import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { Send, ArrowLeft, CheckCircle } from 'lucide-react';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { useConfig } from '../../contexts/ConfigContext';
import { formatDateLongRu } from '../../utils/dateUtils';
import Footer from '../../components/Footer';
import './ContactFormPage.css';

const ContactFormPage = () => {
    useIframeCommunication();
    const { groupId } = useParams();
    const { config } = useConfig();
    const isMag = config?.code === 'mag';
    const isNasledie = config?.code === 'nasledie';
    const [submitted, setSubmitted] = useState(false);
    const [signupEmailSent, setSignupEmailSent] = useState(false);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        lastName: '',
        firstName: '',
        middleName: '',
        email: '',
        phone: '',
        preferredContact: '',
        promoCode: '',
        privacyConsent: false,
        marketingConsent: false
    });

    const { data: group, isLoading: groupLoading } = useQuery(
        ['publicGroup', groupId],
        () => axios.get(`${API_BASE_URL}/groups/public/${groupId}`).then(res => res.data)
    );

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const validatePhone = (phone) => {
        const re = /^[\d\s+\-()]+$/;
        return phone.length >= 10 && re.test(phone);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Валидация полей
        if (!formData.lastName.trim()) {
            toast.error('Введите фамилию');
            return;
        }

        if (!formData.firstName.trim()) {
            toast.error('Введите имя');
            return;
        }

        if (!formData.email.trim()) {
            toast.error('Введите email');
            return;
        }

        if (!validateEmail(formData.email)) {
            toast.error('Введите корректный email');
            return;
        }

        if (!formData.phone.trim()) {
            toast.error('Введите номер телефона');
            return;
        }

        if (!validatePhone(formData.phone)) {
            toast.error('Введите корректный номер телефона (минимум 10 цифр)');
            return;
        }

        if (!isNasledie && !formData.preferredContact.trim()) {
            toast.error('Укажите предпочитаемый способ связи');
            return;
        }

        if (!formData.privacyConsent) {
            toast.error('Необходимо дать согласие на обработку персональных данных');
            return;
        }

        setLoading(true);

        try {
            // Объединяем ФИО в одно поле
            const fullName = `${formData.lastName} ${formData.firstName}${formData.middleName ? ' ' + formData.middleName : ''}`.trim();

            const response = await axios.post(`${API_BASE_URL}/applications`, {
                group_id: groupId,
                name: fullName,
                email: formData.email,
                phone: formData.phone,
                preferred_contact: isNasledie ? undefined : formData.preferredContact.trim(),
                promo_code: isMag ? formData.promoCode : undefined,
                privacyConsent: formData.privacyConsent,
                marketingConsent: formData.marketingConsent
            });

            setSignupEmailSent(Boolean(response.data?.signup_email_sent));
            setSubmitted(true);
            toast.success('Заявка успешно отправлена!');
        } catch (error) {
            console.error('Ошибка отправки заявки:', error);
            toast.error(error.response?.data?.message || 'Ошибка отправки заявки');
        } finally {
            setLoading(false);
        }
    };

    if (groupLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!group) {
        return (
            <div className="error-page">
                <h2>Группа не найдена</h2>
                <Link to="/public/groups" className="btn btn-primary">
                    Вернуться к списку групп
                </Link>
            </div>
        );
    }

    if (group.recruitment_status === 'closed') {
        return (
            <div className="contact-form-page">
                <div className="contact-form-container">
                    <Link to={`/g/${groupId}`} className="back-link">
                        <ArrowLeft size={18} />
                        Назад к группе
                    </Link>
                    <div className="form-header">
                        <h1>Набор закрыт</h1>
                        <p>Заявки в эту группу сейчас не принимаются.</p>
                    </div>
                </div>
            </div>
        );
    }

    const isWebinar = /вебинар/i.test(group.direction_name || '');

    if (submitted) {
        return (
            <div className="contact-form-page">
                <div className="success-message">
                    <CheckCircle size={64} className="success-icon" />
                    <h2>Заявка успешно отправлена!</h2>
                    {isWebinar ? (
                        <>
                            <p>Мы получили вашу заявку на вебинар.</p>
                            <p>
                                Ссылка на онлайн-вебинар придёт <strong>в день проведения</strong> на указанную вами почту.
                            </p>
                        </>
                    ) : (
                        <p>Мы свяжемся с вами в ближайшее время.</p>
                    )}
                    {signupEmailSent && (
                        <p>На почту <strong>{formData.email}</strong> отправлено письмо с информацией.</p>
                    )}
                    <div className="success-actions">
                        <Link to={`/g/${groupId}`} className="btn btn-primary">
                            Вернуться к группе
                        </Link>
                        <Link to="/public/groups" className="btn btn-secondary">
                            К списку групп
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="contact-form-page">
            <div className="contact-form-container">
                <Link to={`/g/${groupId}`} className="back-link">
                    <ArrowLeft size={18} />
                    Назад к группе
                </Link>

                <div className="form-header">
                    <h1>ЗАПИСЬ В ГРУППУ</h1>
                    <div className="group-info-brief">
                        <p className="group-direction">{group.direction_name || 'Группа'}</p>
                        {group.nomination && <p className="group-nomination">{group.nomination}</p>}
                        {group.earliest_event_date && (
                            <p className="group-date">Старт: {formatDateLongRu(group.earliest_event_date)}</p>
                        )}
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="contact-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="lastName">
                                Фамилия <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                required
                                placeholder="Иванов"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="firstName">
                                Имя <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                placeholder="Иван"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="middleName">
                                Отчество
                            </label>
                            <input
                                type="text"
                                id="middleName"
                                name="middleName"
                                value={formData.middleName}
                                onChange={handleChange}
                                placeholder="Иванович"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">
                            Email <span className="required">*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="ivan@example.com"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">
                            Телефон <span className="required">*</span>
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="+7 (999) 123-45-67"
                        />
                    </div>

                    {!isNasledie && (
                    <div className="form-group">
                        <label htmlFor="preferredContact">
                            Предпочитаемый способ связи <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="preferredContact"
                            name="preferredContact"
                            value={formData.preferredContact}
                            onChange={handleChange}
                            required
                            maxLength={255}
                            placeholder="@username, звонок..."
                        />
                        <small className="form-hint">
                            Укажите ник в мессенджере или что удобнее: звонок, SMS и т.п.
                        </small>
                    </div>
                    )}

                    {isMag && (
                        <div className="form-group">
                            <label htmlFor="promoCode">
                                Промокод
                            </label>
                            <input
                                type="text"
                                id="promoCode"
                                name="promoCode"
                                value={formData.promoCode}
                                onChange={handleChange}
                                placeholder="Если есть"
                            />
                        </div>
                    )}

                    <div className="form-check-group">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                id="privacyConsent"
                                name="privacyConsent"
                                checked={formData.privacyConsent}
                                onChange={(e) => setFormData(prev => ({ ...prev, privacyConsent: e.target.checked }))}
                                required
                            />
                            <label htmlFor="privacyConsent">
                                Даю{' '}
                                <a
                                    href={config?.privacyPolicyUrlConsent || config?.privacyPolicyUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="consent-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    согласие
                                </a>
                                {' на обработку моих персональных данных на условиях '}
                                <a
                                    href={config?.privacyPolicyUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="consent-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    политики обработки персональных данных
                                </a>
                                <span className="required">*</span>
                            </label>
                        </div>

                        <div className="form-check">
                            <input
                                type="checkbox"
                                id="marketingConsent"
                                name="marketingConsent"
                                checked={formData.marketingConsent}
                                onChange={(e) => setFormData(prev => ({ ...prev, marketingConsent: e.target.checked }))}
                            />
                            <label htmlFor="marketingConsent">
                                Даю{' '}
                                <a
                                    href={config?.Marketing || config?.privacyPolicyUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="consent-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    согласие
                                </a>
                                {' на получение рекламных рассылок и '}
                                <a
                                    href={config?.MaketingConsent || config?.privacyPolicyUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="consent-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    согласие на обработку
                                </a>
                                {' моих персональных данных в рекламных целях'}
                            </label>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="btn btn-primary btn-submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <div className="spinner-small"></div>
                                Отправка...
                            </>
                        ) : (
                            <>
                                <Send size={18} />
                                Отправить
                            </>
                        )}
                    </button>
                </form>
            </div>
            <Footer />
        </div>
    );
};

export default ContactFormPage;

